import {
    UpdateKEKInputOffer,
    UpdateKEKInputOfferVariables,
} from "_graphql/kek/mutation/__generated__/UpdateKEKInputOffer";
import { useMutation, useReactiveVar } from "@apollo/client";

import { StepFormBuilder } from "../../..";
import { FormItem } from "components/core/step-form-builder";
import { updateKEKPolicy } from "_graphql/kek/mutation/index.kek";
import { currentUserVar } from "_graphql/cache/auth";
import toast from "react-hot-toast";
import { useEffect, useMemo } from "react";
import { useFormik } from "formik";
import OfferDetailsKEKForm from "../offer-details-form-kek";
import OfferDetailsSummary from "../offer-details-summary";
import PlacingSlipEditForm from "../placing-slip-edit-form";
import PlacingSlipSummary from "../placing-slip-summary";
import { buildKEKOfferInput } from "../utils";
import { updateDetailsInitialValues } from "../validation";
import useApi from "utils/use-api";
import { checkPlacingSlipExists } from "../placing-slip-api";

type Props = {
    setClose?: (value: boolean) => void;
    refetch?: () => void;
    offer?: any;
    offerId?: string;
};

const UpdateSlipKEKWizardContainer = ({
    setClose,
    refetch,
    offer,
    offerId,
}: Props) => {
    const api = useApi();
    const user = useReactiveVar(currentUserVar);
    const resolvedOfferId = offerId || offer?.offer_id || "";

    const formSteps: FormItem[] = useMemo(
        () => [
            {
                accessor: "details",
                description: "Update the offer details",
                name: "Offer Details",
                FormComponent: OfferDetailsKEKForm,
                SummaryComponent: OfferDetailsSummary,
            },
            {
                accessor: "placingSlip",
                description: "Upload placing slip for this offer",
                name: "Upload Placing Slip",
                FormComponent: (props: any) => (
                    <PlacingSlipEditForm
                        {...props}
                        offerId={resolvedOfferId}
                    />
                ),
                SummaryComponent: PlacingSlipSummary,
            },
        ],
        [resolvedOfferId],
    );

    const [update] = useMutation<
        UpdateKEKInputOffer,
        UpdateKEKInputOfferVariables
    >(updateKEKPolicy, {
        refetchQueries: ["Offers"],
    });

    const form = useFormik<any>({
        initialValues: {
            details: updateDetailsInitialValues,
            placingSlip: { pendingFiles: [], hasExisting: false },
            placingSlipBusy: false,
            offerId: resolvedOfferId,
        },
        onSubmit: async (values) => {
            const employeeId = user?.employee?.employee_id ?? "";

            try {
                const { data } = await update({
                    variables: {
                        offerDetailId:
                            offer?.offer_detail?.offer_detail_id || "",
                        offerId: resolvedOfferId,
                        offerInput: buildKEKOfferInput(
                            values.details,
                            employeeId,
                            true,
                        ),
                    },
                });

                if (!data?.updateInputOffer) {
                    toast.error("Could not update Offer");
                    return;
                }

                toast.success("Offer updated Successfully");
                refetch?.();
                setClose?.(false);
            } catch {
                toast.error("Could not update Offer");
            }
        },
    });

    useEffect(() => {
        if (offer) {
            form.setFieldValue("details", {
                createForRetroCedent: Boolean(
                    offer?.offer_retrocedent?.reinsurer?.reinsurer_id,
                ),
                include_nkrol_comment: Boolean(
                    offer?.offer_detail?.information_comment,
                ),
                addExchangeRate: Boolean(offer?.exchange_rate?.ex_currency),
                class_of_business_id: offer.classofbusiness,
                old_class_of_business_id:
                    offer.classofbusiness?.class_of_business_id,
                offer_details: JSON.parse(
                    offer.offer_detail?.offer_details || "[]",
                ),
                old_offer_detail: JSON.parse(
                    offer.offer_detail?.offer_details || "[]",
                ),
                insurer_id: offer.insurer?.insurer_id,
                premium: offer.premium,
                policy_number: offer?.offer_detail?.policy_number,
                insured_by: offer?.offer_detail?.insured_by,
                sum_insured: offer.sum_insured,
                rate: offer.rate,
                facultative_offer: offer.facultative_offer,
                commission: offer.commission,
                brokerage: offer.brokerage,
                currency: offer.offer_detail?.currency,
                period_of_insurance_from:
                    offer.offer_detail?.period_of_insurance_from,
                period_of_insurance_to:
                    offer.offer_detail?.period_of_insurance_to,
                comment: offer?.offer_detail?.offer_comment || "",
                reinsurer_id:
                    offer?.offer_retrocedent?.reinsurer?.reinsurer_id || "",
                business_state: offer?.business_state || "",
                co_insurance_share: offer?.co_insurance_share || "",
                ex_currency: offer?.exchange_rate?.ex_currency || "",
                ex_rate: offer?.exchange_rate?.ex_rate || "",
                information_comment:
                    offer?.offer_detail?.information_comment || "",
                payment_type:
                    Object.keys(
                        JSON.parse(offer?.offer_detail?.payment_type || "{}"),
                    )?.at(0) || "payable_in_full",
                no_of_installments:
                    Object.values(
                        JSON.parse(offer?.offer_detail?.payment_type || "{}"),
                    )?.at(0) || 1,
            });
        }
    }, [offer]);

    useEffect(() => {
        if (!resolvedOfferId) return;

        let active = true;

        const loadPlacingSlipState = async () => {
            try {
                const exists = await checkPlacingSlipExists(api, resolvedOfferId);
                if (!active) return;
                form.setFieldValue("placingSlip", {
                    pendingFiles: [],
                    hasExisting: exists,
                });
            } catch {
                if (!active) return;
            }
        };

        loadPlacingSlipState();

        return () => {
            active = false;
        };
    }, [resolvedOfferId]);

    return (
        <StepFormBuilder
            initialStep={0}
            formSteps={formSteps}
            form={form}
            setOpen={setClose}
        />
    );
};

export default UpdateSlipKEKWizardContainer;
